import React, { useRef, useEffect, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';
import ListingCard from './ListingCard';
import { Listing } from '../types';

import 'swiper/css';
import 'swiper/css/pagination';
import './ListingsCarousel.css';

interface ListingsCarouselProps {
  listings: Listing[];
  limit?: number;
}

const ListingsCarousel: React.FC<ListingsCarouselProps> = ({ listings, limit = 10 }) => {
  const [isVisible, setIsVisible] = useState(false);
  const swiperRef = useRef<SwiperType | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Sort by sold date (most recent first) and limit
  const sortedListings = [...listings]
    .sort((a, b) => {
      const dateA = a.soldDate ? new Date(a.soldDate).getTime() : 0;
      const dateB = b.soldDate ? new Date(b.soldDate).getTime() : 0;
      return dateB - dateA;
    })
    .slice(0, limit);

  // Intersection Observer for visibility-based autoplay
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      { threshold: 0.3 }
    );

    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  // Start/stop autoplay based on visibility
  useEffect(() => {
    if (!swiperRef.current) return;

    if (isVisible && swiperRef.current.params.autoplay) {
      swiperRef.current.autoplay.start();
    } else {
      swiperRef.current.autoplay.stop();
    }
  }, [isVisible]);

  if (sortedListings.length === 0) {
    return null;
  }

  // If only 1 listing, just center it without carousel
  if (sortedListings.length === 1) {
    return (
      <div className="listings-carousel-single">
        <ListingCard listing={sortedListings[0]} />
      </div>
    );
  }

  return (
    <div className="listings-carousel" ref={containerRef}>
      <Swiper
        modules={[Autoplay, Pagination]}
        onSwiper={(swiper) => {
          swiperRef.current = swiper;
          swiper.autoplay.stop();
        }}
        spaceBetween={24}
        slidesPerView={1}
        centeredSlides={sortedListings.length < 3}
        loop={sortedListings.length >= 3}
        speed={600}
        autoplay={{
          delay: 5000,
          disableOnInteraction: false,
        }}
        pagination={{
          clickable: true,
        }}
        breakpoints={{
          640: {
            slidesPerView: sortedListings.length === 1 ? 1 : Math.min(2, sortedListings.length),
            centeredSlides: sortedListings.length < 2,
          },
          1024: {
            slidesPerView: sortedListings.length === 1 ? 1 : Math.min(3, sortedListings.length),
            centeredSlides: sortedListings.length < 3,
          },
        }}
        className="listings-swiper"
      >
        {sortedListings.map((listing) => (
          <SwiperSlide key={listing.id}>
            <ListingCard listing={listing} />
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
};

export default ListingsCarousel;
